/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

// The field's palettes, packed for the shader: the base palette and each live wave's, in
// OKLab, in the current theme's form (palettes.ts). They only upload when they or the theme
// change.

import { parseSrgb, srgbToOklab } from "@bfly/engine";
import type { Theme } from "../page/theme.js";
import { MAX_COLORS, MAX_RINGS } from "./field-shaders.js";
import { PALETTES, themePalette } from "./palettes.js";

// Each palette's colors in OKLab, ready for the shader, in both theme forms.
const PALETTES_OKLAB = PALETTES.map((_, index) => {
  const oklab = (theme: Theme): number[] =>
    themePalette(index, theme)
      .field.slice(0, MAX_COLORS)
      .flatMap((hex) => srgbToOklab(parseSrgb(hex)));
  return { light: oklab("light"), dark: oklab("dark") };
});

/** The field program's palette uniforms. */
export interface PaletteUniforms {
  readonly colors: WebGLUniformLocation | null;
  readonly colorCount: WebGLUniformLocation | null;
  readonly ringColors: WebGLUniformLocation | null;
  readonly ringColorCount: WebGLUniformLocation | null;
}

export class FieldPalettes {
  private base = 0;
  // Each live wave's palette, by the wave's index in the shader's list (-1: it paints none).
  private readonly rings = new Int32Array(MAX_RINGS).fill(-1);
  private readonly ringColors = new Float32Array(MAX_RINGS * MAX_COLORS * 3);
  private readonly ringColorCount = new Int32Array(MAX_RINGS);
  private changed = true;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly u: PaletteUniforms,
  ) {}

  /** Sets the palette outside every live wave. */
  setBase(palette: number): void {
    if (palette === this.base) return;
    this.base = palette;
    this.changed = true;
  }

  /** Sets the palette of the wave at shader index `index`, or -1 for none. */
  setRing(index: number, palette: number): void {
    if (this.rings[index] === palette) return;
    this.rings[index] = palette;
    this.changed = true;
  }

  /** The theme changed, so the next upload picks up its forms. */
  themeChanged(): void {
    this.changed = true;
  }

  /** Uploads the palettes in `theme`'s form, if they or the theme changed. Expects the
      field's program to be current. */
  upload(theme: Theme): void {
    if (!this.changed) return;
    this.changed = false;
    const { gl, u } = this;
    const colors = PALETTES_OKLAB[this.base]?.[theme] ?? [];
    gl.uniform3fv(u.colors, colors);
    gl.uniform1i(u.colorCount, colors.length / 3);
    this.ringColorCount.fill(0);
    for (let i = 0; i < MAX_RINGS; i++) {
      const lab = PALETTES_OKLAB[this.rings[i]!]?.[theme];
      if (!lab) continue;
      this.ringColors.set(lab, i * MAX_COLORS * 3);
      this.ringColorCount[i] = lab.length / 3;
    }
    gl.uniform3fv(u.ringColors, this.ringColors);
    gl.uniform1iv(u.ringColorCount, this.ringColorCount);
  }
}
